import React, { useState } from "react";
import { api, SPECIALTIES, specialtyColor } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { Plus, Trash2, HardHat, UserRound, Check } from "lucide-react";

const inputCls = "w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";

export const WorkAssignments = ({ value = [], onChange, techs = [], subs = [], onSubsReload }) => {
  const { t } = useLang();
  const [newSubIdx, setNewSubIdx] = useState(null);
  const [newSubName, setNewSubName] = useState("");

  const rows = value || [];
  const set = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const add = (type) => onChange([...rows, { type, ref_id: "", name: "", trades: [], value: 0 }]);
  const remove = (i) => { if (newSubIdx === i) setNewSubIdx(null); onChange(rows.filter((_, j) => j !== i)); };
  const toggleTrade = (i, sp) => {
    const cur = rows[i].trades || [];
    set(i, { trades: cur.includes(sp) ? cur.filter((x) => x !== sp) : [...cur, sp] });
  };

  const createSub = async (i) => {
    if (!newSubName.trim()) return;
    try {
      const s = await api.post("/subcontractors", { name: newSubName.trim(), specialties: rows[i].trades || [] });
      set(i, { ref_id: s.id, name: s.name });
      setNewSubIdx(null); setNewSubName("");
      onSubsReload && onSubsReload();
      toast.success(t("saved"));
    } catch { toast.error(t("error")); }
  };

  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50" data-testid="work-assignments">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <label className="text-xs font-bold uppercase tracking-wide text-slate-600">{t("teamByTrade")}</label>
        <div className="flex gap-2">
          <button type="button" data-testid="wa-add-tech" onClick={() => add("technician")} className="inline-flex items-center gap-1 text-xs font-medium text-slate-700 border border-slate-300 rounded-lg px-2.5 py-1.5 hover:bg-white bg-white/60">
            <Plus className="w-3.5 h-3.5" /> {t("waTech")}
          </button>
          <button type="button" data-testid="wa-add-sub" onClick={() => add("subcontractor")} className="inline-flex items-center gap-1 text-xs font-medium text-fuchsia-700 border border-fuchsia-300 rounded-lg px-2.5 py-1.5 hover:bg-fuchsia-50 bg-white/60">
            <Plus className="w-3.5 h-3.5" /> {t("subcontractor")}
          </button>
        </div>
      </div>
      {rows.length === 0 && <p className="text-xs text-slate-400">{t("teamByTradeHint")}</p>}
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} data-testid={`wa-row-${i}`} className="bg-white border border-slate-200 rounded-lg p-2.5 space-y-2">
            <div className="flex items-center gap-2">
              {r.type === "subcontractor"
                ? <HardHat className="w-4 h-4 text-fuchsia-600 shrink-0" />
                : <UserRound className="w-4 h-4 text-slate-500 shrink-0" />}
              {r.type === "technician" ? (
                <select data-testid={`wa-person-${i}`} value={r.ref_id || ""} className={`${inputCls} flex-1`}
                  onChange={(e) => { const u = techs.find((x) => x.id === e.target.value); set(i, { ref_id: u?.id || "", name: u?.name || "" }); }}>
                  <option value="">{t("selectTechnician")}</option>
                  {techs.map((u) => <option key={u.id} value={u.id}>{u.name}{u.specialties?.length ? ` — ${u.specialties.map((s) => t(`sp_${s}`)).join(", ")}` : ""}</option>)}
                </select>
              ) : newSubIdx === i ? (
                <div className="flex items-center gap-1.5 flex-1">
                  <input data-testid={`wa-new-sub-name-${i}`} autoFocus value={newSubName} onChange={(e) => setNewSubName(e.target.value)}
                    placeholder={t("name")} className={`${inputCls} flex-1`} onKeyDown={(e) => e.key === "Enter" && createSub(i)} />
                  <button type="button" data-testid={`wa-new-sub-save-${i}`} onClick={() => createSub(i)} className="p-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700"><Check className="w-4 h-4" /></button>
                  <button type="button" onClick={() => { setNewSubIdx(null); setNewSubName(""); }} className="text-xs text-slate-400 hover:text-slate-600 px-1">{t("cancel")}</button>
                </div>
              ) : (
                <select data-testid={`wa-person-${i}`} value={r.ref_id || (r.name ? "__legacy__" : "")} className={`${inputCls} flex-1`}
                  onChange={(e) => {
                    if (e.target.value === "__new__") { setNewSubIdx(i); setNewSubName(""); return; }
                    if (e.target.value === "__legacy__") return;
                    const s = subs.find((x) => x.id === e.target.value);
                    set(i, { ref_id: s?.id || "", name: s?.name || "" });
                  }}>
                  <option value="">{t("selectSubcontractor")}</option>
                  {r.ref_id && !subs.some((s) => s.id === r.ref_id) && <option value={r.ref_id}>{r.name || "—"}</option>}
                  {!r.ref_id && r.name && <option value="__legacy__">{r.name}</option>}
                  {subs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  <option value="__new__">＋ {t("registerNewSub")}</option>
                </select>
              )}
              {r.type === "subcontractor" && newSubIdx !== i && (
                <input data-testid={`wa-value-${i}`} type="number" step="0.01" value={r.value || ""} placeholder="$"
                  onChange={(e) => set(i, { value: parseFloat(e.target.value) || 0 })}
                  className="w-24 shrink-0 rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm text-right focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition" />
              )}
              <button type="button" data-testid={`wa-remove-${i}`} onClick={() => remove(i)} className="p-1.5 rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 shrink-0"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="flex flex-wrap gap-1.5 pl-6">
              {SPECIALTIES.map((sp) => {
                const on = (r.trades || []).includes(sp);
                return (
                  <button key={sp} type="button" data-testid={`wa-trade-${i}-${sp}`} onClick={() => toggleTrade(i, sp)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${on ? "bg-slate-900 text-white" : `${specialtyColor(sp)} hover:opacity-80`}`}>
                    {t(`sp_${sp}`)}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
